<script>
    import { onMount } from 'svelte';
    import { get } from 'svelte/store';
    import { currentPage, recommendationsStore, userStore } from '../store.js';
    import { navigate } from '../router.js';
    import { formDataStore } from '../components/store';
    import Banner from '../components/Banner.svelte';
    import Button from '../components/Button.svelte';
    import Card from '../components/Card.svelte';
    import Chip from '../components/Chip.svelte';
    import EmptyState from '../components/EmptyState.svelte';
    import Stat from '../components/Stat.svelte';
    import {
        CONCERN_OPTIONS,
        engineProfile,
        loadProfile,
        persistProfile,
        saveProfileToServer,
        ensureUserId,
        safeParse
    } from '../profile.js';

    export let goToRecommendations;

    const improvementToStrengthMapping = {
        wrinkles: 'Smooth Skin',
        acne: 'Clear Skin',
        hyperpigmentation: 'Even Skin Tone',
        'under eye circles': 'Radiant Under Eyes',
        'under eye wrinkles': 'Firm Under Eyes',
        'dry skin': 'Hydrated Skin',
        'face redness': 'Calm Skin Tone',
        'rough texture': 'Smooth Texture',
        'oily skin': 'Balanced Skin',
        'large pores': 'Refined Pores',
        'dull skin': 'Radiant Skin'
    };

    let AOIareas = [];
    let AOSareas = [];
    let selectedConcerns = [];
    let skinType = 'normal';
    // Whether the person has been through the questions step yet, so the
    // screen can offer the right next action.
    let answered = false;
    let statusMessage = '';
    let isLoadingRecommendations = false;
    let isSavingProfile = false;
    let analysisSaveStarted = false;
    let base64Selfie = '';
    let hasAnalysis = true;
    // Someone who already told us their routine is re-rating it against this
    // photo; someone who has not is on step 2 of onboarding.
    let hasRoutine = false;

    onMount(() => {
        base64Selfie = localStorage.getItem('base64Selfie') || '';
        const AOIareasJSON = localStorage.getItem('AoI');
        const AOSareasJSON = localStorage.getItem('AoS');

        if (AOIareasJSON) {
            AOIareas = safeParse(AOIareasJSON, []);
        }

        if (AOSareasJSON) {
            AOSareas = safeParse(AOSareasJSON, []);
        }

        const profile = loadProfile();
        selectedConcerns = profile.areasOfImprovement;
        skinType = profile.skinType;
        answered = profile.answered;
        hasRoutine = safeParse(localStorage.getItem('currentRoutine') || '[]', []).length > 0;

        // Reaching this page with nothing stored -- a stale bookmark, cleared
        // storage, or a deep link -- must not render "Your skin profile is
        // ready" over an empty form.
        hasAnalysis = selectedConcerns.length > 0 || AOSareas.length > 0;
        if (!hasAnalysis) return;

        ensureUserId();
        persistSkinProfile();
        saveAnalysisInBackground();
    });

    function startAnalysis() {
        navigate('face-analysis');
    }

    function toTitleCase(str) {
        return (str || '').replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
    }

    function getPositiveTerm(area) {
        return improvementToStrengthMapping[area] || toTitleCase(area);
    }

    function dataURItoBlob(dataURI) {
        const byteString = atob(dataURI);
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }
        return new Blob([ab], { type: 'image/png' });
    }

    async function saveAnalysisInBackground() {
        if (analysisSaveStarted) return;
        analysisSaveStarted = true;

        const base64ImageString = localStorage.getItem('base64Selfie');
        if (!base64ImageString) return;

        try {
            const formData = new FormData();
            formData.append('picture', dataURItoBlob(base64ImageString), 'selfie.png');
            formData.append('AOIAreas', JSON.stringify(selectedConcerns.map(concern => ({ concern }))));
            formData.append('AOSAreas', JSON.stringify(AOSareas));
            formData.append('userId', ensureUserId());

            const uploadResponse = await fetch('/api/upload_picture', {
                method: 'POST',
                body: formData,
                credentials: 'include'
            });

            if (uploadResponse.ok) {
                const data = await uploadResponse.json();
                userStore.setUserId(data.userId);
            }
        } catch (error) {
            console.error('Error saving analysis in background:', error);
        }
    }

    // The profile is written on arrival so an abandoned session still carries
    // the analysis. The questions step fills in the rest.
    function persistSkinProfile() {
        return persistProfile(loadProfile());
    }

    function scoreFor(concern) {
        const match = AOIareas.find(item => item && item.concern === concern);
        return match && typeof match.score === 'number' ? match.score : null;
    }

    // Severity bands. The analyzer returns a 0-1 score per concern; showing
    // the raw decimal means nothing to a user, so bucket it into language.
    function severityFor(score) {
        if (score === null || score === undefined) return { label: 'Detected', rank: 1, tone: 'mild' };
        // "Most noticeable" is a rank, not a band, so it cannot be the label
        // for a band: with scores of .64, .48 and .41 the screen said "most
        // noticeable" three times. These are strengths, and the ranking is
        // carried by the order and the numerals instead.
        if (score >= 0.40) return { label: 'Strong', rank: 3, tone: 'high' };
        if (score >= 0.25) return { label: 'Moderate', rank: 2, tone: 'medium' };
        return { label: 'Mild', rank: 1, tone: 'mild' };
    }

    // What the analyzer actually found, strongest first -- this is the thing
    // the user came for, so it leads the page.
    $: findings = selectedConcerns
        .map(concern => {
            const score = scoreFor(concern);
            return { concern, label: toTitleCase(concern), score, severity: severityFor(score) };
        })
        .sort((a, b) => (b.score ?? 0) - (a.score ?? 0));

    $: headlineConcern = findings.length ? findings[0].label : '';
    $: scannedConcernCount = CONCERN_OPTIONS.length;

    // Severity -> the shared tone vocabulary, so a card's top rule, its chip
    // and its meter all say the same thing without anyone reading a number.
    function toneFor(finding) {
        if (finding.score === null || finding.score === undefined) return 'default';
        if (finding.severity.tone === 'high') return 'danger';
        if (finding.severity.tone === 'medium') return 'caution';
        return 'success';
    }

    function percentFor(finding) {
        if (finding.score === null || finding.score === undefined) return null;
        return Math.min(100, Math.round(finding.score * 100));
    }

    async function saveUserProfile() {
        return saveProfileToServer(loadProfile());
    }

    async function isAuthenticated() {
        const response = await fetch('/api/is_authenticated', { credentials: 'include' });
        return response.ok;
    }

    function redirectToSignupForRecommendations(profile) {
        localStorage.setItem('pendingRecommendationRequest', 'true');
        localStorage.setItem('formDataForRecommendations', JSON.stringify(engineProfile(profile)));
        window.location.href = `/api/signup?currentPage=recommendations&oldUserId=${encodeURIComponent(profile.userId)}`;
    }

    async function showRoutine() {
        isLoadingRecommendations = true;
        statusMessage = '';

        try {
            const profile = await saveUserProfile();
            const authenticated = await isAuthenticated();
            const isLocalDev = ['localhost', '127.0.0.1'].includes(window.location.hostname);

            if (!authenticated && !isLocalDev) {
                statusMessage = 'Saving your Skin Profile before building your routine...';
                redirectToSignupForRecommendations(profile);
                return;
            }

            const recoResponse = await fetch('/api/recommendation_engine', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(engineProfile(profile))
            });

            if (!recoResponse.ok) {
                throw new Error('Failed to load recommendations');
            }

            const recommendations = await recoResponse.json();
            await fetch('/api/store_recommendations', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: profile.userId,
                    recommendations,
                    formData: profile
                })
            });

            recommendationsStore.set(recommendations);
            goToRecommendations(recommendations);
        } catch (error) {
            console.error('Error building routine:', error);
            statusMessage = 'Unable to build your routine right now. Please try again.';
        } finally {
            isLoadingRecommendations = false;
        }
    }

    // The questions that used to sit at the bottom of this page. They decide
    // what the routine includes, so they are a step of their own now.
    async function goToQuestions() {
        persistSkinProfile();
        navigate('skin-questions');
    }

    async function checkProduct() {
        try {
            await saveUserProfile();
        } catch (error) {
            console.error('Error saving profile before scanner:', error);
        }
        navigate('scanner');
    }

    async function importRoutine() {
        try {
            await saveUserProfile();
        } catch (error) {
            console.error('Error saving profile before routine import:', error);
        }
        if (hasRoutine) {
            localStorage.removeItem('onboardingStep');
        } else {
            localStorage.setItem('onboardingStep', 'routine');
        }
        navigate('routine-import');
    }

    async function saveProfile() {
        isSavingProfile = true;
        statusMessage = '';
        try {
            const profile = await saveUserProfile();
            const authenticated = await isAuthenticated();
            if (!authenticated) {
                statusMessage = 'Create your free account to save this Skin Profile permanently.';
                window.location.href = `/api/signup?currentPage=profile&oldUserId=${encodeURIComponent(profile.userId)}`;
                return;
            }
            statusMessage = 'Your Skin Profile has been saved.';
        } catch (error) {
            console.error('Error saving skin profile:', error);
            statusMessage = 'Unable to save your Skin Profile right now.';
        } finally {
            isSavingProfile = false;
        }
    }
</script>

<style>
    /* The page is one column of beats: what we found, what that reading was
       made of, what to do next, then the controls that let you correct it.
       Everything that used to need a 620 / 640 / 768 / 900 / 1180 breakpoint
       is now an intrinsic grid or a clamp(). The one media query left is the
       thumb-reach CTA bar, which is genuinely a phone-only object. */

    .my-skin {
        font-family: var(--font-body);
    }

    /* One rhythm between the page's beats, from the token that exists for it. */
    .my-skin > * + * {
        margin-top: var(--section-gap);
    }

    /* One object on the screen, one way out of it. */
    .empty-wrap {
        max-width: var(--prose-max);
        margin: 0 auto;
    }

    /* --- header ---------------------------------------------------------- */

    .eyebrow {
        color: var(--rose);
        font-size: var(--text-2xs);
        letter-spacing: var(--track-eyebrow);
        text-transform: uppercase;
        margin: 0 0 var(--space-5);
    }

    h1 {
        font-family: var(--font-display);
        font-size: var(--text-4xl);
        font-weight: var(--weight-regular);
        line-height: var(--leading-snug);
        color: var(--cream);
        margin: 0 0 var(--space-5);
        max-width: 18ch;
    }

    .intro {
        color: var(--text-muted);
        font-size: var(--text-md);
        line-height: var(--leading-relaxed);
        max-width: var(--prose-max);
        margin: 0;
    }

    .intro strong {
        color: var(--cream);
        font-weight: var(--weight-medium);
    }

    /* --- section furniture ----------------------------------------------- */

    h2 {
        font-family: var(--font-display);
        font-size: var(--text-lg);
        font-weight: var(--weight-regular);
        line-height: var(--leading-snug);
        color: var(--cream);
        margin: 0 0 var(--space-3);
    }

    h2.lead {
        font-size: var(--text-2xl);
    }

    .label {
        font-size: var(--text-2xs);
        letter-spacing: var(--track-label);
        text-transform: uppercase;
        color: var(--text-muted);
        margin: 0 0 var(--space-3);
    }

    .helper {
        color: var(--text-muted);
        font-size: var(--text-sm);
        line-height: var(--leading-relaxed);
        max-width: var(--prose-max);
        margin: 0 0 var(--space-6);
    }

    .note {
        color: var(--text-muted);
        font-size: var(--text-xs);
        line-height: var(--leading-normal);
        max-width: var(--prose-max);
        margin: var(--space-6) 0 0;
    }

    /* --- findings: the payoff -------------------------------------------- */

    .findings {
        list-style: none;
        margin: 0;
        padding: 0;
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
        gap: var(--space-4);
    }

    .finding-rank {
        font-size: var(--text-3xs);
        letter-spacing: var(--track-label);
        color: var(--text-muted);
        margin: 0 0 var(--space-3);
    }

    .finding-name {
        font-family: var(--font-display);
        font-size: var(--text-xl);
        font-weight: var(--weight-regular);
        line-height: var(--leading-snug);
        color: var(--cream);
        margin: 0 0 var(--space-5);
    }

    .meter {
        height: 2px;
        background: var(--border);
        margin-bottom: var(--space-5);
    }

    .meter span {
        display: block;
        height: 100%;
        min-width: 3px;
    }

    .meter span.danger  { background: var(--danger); }
    .meter span.caution { background: var(--caution); }
    .meter span.success { background: var(--success); }
    .meter span.default { background: var(--border-strong); }

    /* --- the reading it came from ---------------------------------------- */

    .reading {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
        gap: clamp(24px, 4vw, 48px);
        align-items: start;
    }

    /* Capped, so the evidence supports the findings instead of outshouting
       them. The frame keeps its ratio whatever the camera sent. */
    .scan {
        margin: 0;
        max-width: 340px;
    }

    /* aspect-ratio + contain: the photo is never stretched and never cropped,
       whatever the camera handed us. The old rule was a 520px max-height. */
    .scan-frame {
        aspect-ratio: 4 / 5;
        background: var(--bg);
        border: 1px solid var(--border);
        display: grid;
        place-items: center;
        overflow: hidden;
    }

    .scan-frame img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: contain;
    }

    .scan-caption {
        color: var(--text-muted);
        font-size: var(--text-xs);
        line-height: var(--leading-normal);
        margin: var(--space-4) 0 0;
    }

    .skin-type {
        font-family: var(--font-display);
        font-size: var(--text-2xl);
        font-weight: var(--weight-regular);
        line-height: var(--leading-snug);
        color: var(--cream);
        margin: 0 0 var(--space-6);
    }

    .stats {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
        gap: var(--space-6);
    }

    /* --- next step -------------------------------------------------------- */

    .actions {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-3);
    }

    .status {
        color: var(--rose);
        font-size: var(--text-xs);
        line-height: var(--leading-normal);
        margin: var(--space-5) 0 0;
        min-height: 22px;
    }

    .chip-row {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-2);
    }

    /* --- phone-only: keep the one primary action in thumb reach ----------- */

    .cta-bar { display: none; }

    @media (max-width: 768px) {
        .cta-bar {
            display: block;
            position: fixed;
            left: 0;
            right: 0;
            bottom: 0;
            z-index: var(--z-menu);
            padding: var(--space-3) var(--space-4) calc(var(--space-3) + env(safe-area-inset-bottom, 0));
            background: var(--scrim);
            backdrop-filter: blur(18px);
            border-top: 1px solid var(--border);
        }

        .my-skin {
            padding-bottom: 128px;
        }
    }
</style>

<div class="u-page my-skin">
{#if !hasAnalysis}
  <div class="empty-wrap">
    <EmptyState
        title="No skin analysis yet."
        body="We don’t have an analysis saved in this browser. Take a quick selfie and SkinGenie will read your skin, then build a routine around what it finds."
    >
        <Button on:click={startAnalysis}>Analyze my skin</Button>
    </EmptyState>
  </div>
{:else}
    <header>
        <p class="eyebrow">{hasRoutine ? 'Your skin analysis' : 'Step 1 of 4 · Your skin analysis'}</p>
        <h1>Here’s what we found.</h1>
        <p class="intro">
            We looked at {scannedConcernCount} skin characteristics.
            {#if headlineConcern}
                <strong>{headlineConcern}</strong> stood out most, and your skin reads as
                <strong>{toTitleCase(skinType).toLowerCase()}</strong>.
            {/if}
        </p>
    </header>

    <section aria-labelledby="findings-title">
        <h2 class="lead" id="findings-title">What we found</h2>
        <p class="helper">Ranked by how strongly each one showed up in your photo.</p>
        <ol class="findings">
            {#each findings as finding, i}
                <li>
                    <Card tone={toneFor(finding)}>
                        <p class="finding-rank">{(i + 1 + '').padStart(2, '0')}</p>
                        <h3 class="finding-name">{finding.label}</h3>
                        {#if percentFor(finding) !== null}
                            <div class="meter" aria-hidden="true">
                                <span class={toneFor(finding)} style="width: {percentFor(finding)}%"></span>
                            </div>
                        {/if}
                        <Chip
                            tone={toneFor(finding)}
                            value={percentFor(finding) === null ? null : percentFor(finding) + '%'}
                        >{finding.severity.label}</Chip>
                    </Card>
                </li>
            {/each}
        </ol>
        <p class="note">
            A camera reading of one photo, not a diagnosis. Nothing here replaces a
            dermatologist.
        </p>
    </section>

    <section class="reading" aria-label="What this reading was based on">
        <figure class="scan">
            {#if base64Selfie}
                <div class="scan-frame">
                    <img alt="Your selfie" src={"data:image/png;base64," + base64Selfie} />
                </div>
            {:else}
                <Banner tone="info" title="Photo not saved">
                    Your results are still from this scan.
                </Banner>
            {/if}
            <figcaption class="scan-caption">
                This analysis stays attached to your profile when you save or sign in. Your photo
                is never shared with brands or retailers, and you can delete it at any time,
                see our <a href="/privacy#photos">photo policy</a>.
            </figcaption>
        </figure>

        <div>
            <p class="label">Optimizing for</p>
            <p class="skin-type">{toTitleCase(skinType)} skin</p>
            <div class="stats">
                <Stat align="start" value={findings.length} label="Concerns detected" />
                <Stat align="start" value={scannedConcernCount} label="Characteristics scanned" />
            </div>
            <p class="note">
                Based on {findings.length} concern{findings.length === 1 ? '' : 's'} detected in this
                scan. Scan again in a few weeks to see what changes.
            </p>
        </div>
    </section>

    <section aria-labelledby="next-title">
        <h2 class="lead" id="next-title">Next step</h2>
        {#if hasRoutine}
            <p class="helper">
                See how your current routine holds up against this photo. SkinGenie rates it
                for your skin and tells you what to change first.
            </p>
        {:else}
            <p class="helper">
                A few quick questions next, so the routine is built for your skin and not an
                average one. It takes about a minute.
            </p>
        {/if}
        <div class="actions">
            {#if hasRoutine}
                <Button on:click={importRoutine}>Rate my routine</Button>
                <Button
                    variant="secondary"
                    on:click={showRoutine}
                    loading={isLoadingRecommendations}
                    busyLabel="Building your routine"
                >Build a new routine</Button>
            {:else}
                <Button on:click={goToQuestions}>Continue</Button>
            {/if}
            <Button variant="secondary" on:click={checkProduct}>Check a product</Button>
            {#if answered || hasRoutine}
                <Button variant="ghost" on:click={goToQuestions}>Edit my details</Button>
            {/if}
            <Button
                variant="ghost"
                on:click={saveProfile}
                loading={isSavingProfile}
                busyLabel="Saving your profile"
            >Save profile</Button>
        </div>
        <p class="status" aria-live="polite">{statusMessage}</p>
    </section>

    {#if AOSareas.length > 0}
        <section aria-labelledby="strengths-title">
            <h2 class="lead" id="strengths-title">Strengths</h2>
            <p class="helper">Places your skin is already doing well.</p>
            <div class="chip-row">
                {#each AOSareas as area}
                    <Chip tone="success">{getPositiveTerm(area.concern)}</Chip>
                {/each}
            </div>
        </section>
    {/if}

    <!-- On a phone the page is a long scroll, so keep the one primary action
         within thumb reach instead of five screens down. -->
    <div class="cta-bar">
        <Button full on:click={hasRoutine ? importRoutine : goToQuestions}>{hasRoutine ? 'Rate my routine' : 'Continue'}</Button>
    </div>
{/if}
</div>
